import { useSectionAccess } from "../../auth/useSectionAccess";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  tmApi,
  type TmProjectMemberRole,
  type TmProjectMemberView,
  type TmProjectView,
  type TmTaskStatus,
  type TmTaskView,
  type TmUserOption,
} from "../../api/tm";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import { TmStatusBadge } from "../../components/tm/TmStatusBadge";
import { TmProjectStatusDropdown } from "../../components/tm/TmProjectStatusDropdown";
import { TmPriorityPill } from "../../components/tm/TmPriorityPill";
import { UserCombobox } from "../../components/tm/UserCombobox";
import { useAuth } from "../../auth/AuthContext";
import type { ApiError } from "../../api/client";

export default function TmProjectDetailPage() {
  const { canWrite, canFull } = useSectionAccess("tm_projects");
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission, state } = useAuth();
  const isTmAdmin = hasPermission("tm:admin");
  const canCreateTask = hasPermission("section:tm_tasks:write");
  const myId = state.status === "authenticated" ? state.user.id : null;

  const [project, setProject] = useState<TmProjectView | null>(null);
  const [members, setMembers] = useState<TmProjectMemberView[]>([]);
  const [tasks, setTasks] = useState<TmTaskView[]>([]);
  const [users, setUsers] = useState<TmUserOption[]>([]);
  const [pickUser, setPickUser] = useState<string | null>(null);
  const [pickRole, setPickRole] = useState<TmProjectMemberRole>("CONTRIBUTOR");
  const [taskStatusFilter, setTaskStatusFilter] = useState<TmTaskStatus | "ALL">("ALL");
  const [assigneeFilter, setAssigneeFilter] = useState<Set<string>>(new Set());
  const [showMembers, setShowMembers] = useState(false);
  const [taskView, setTaskView] = useState<"list" | "board">("list");

  const refresh = useCallback(async () => {
    if (!id) return;
    const [p, ms, ts] = await Promise.all([
      tmApi.getProject(id),
      tmApi.listProjectMembers(id),
      tmApi.byProject(id),
    ]);
    setProject(p);
    setMembers(ms);
    setTasks(ts);
  }, [id]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    void tmApi.users().then(setUsers).catch(() => undefined);
  }, []);

  async function addMember() {
    if (!canFull || !(isTmAdmin || isProjectOwner) || !id || !pickUser) return;
    try {
      await tmApi.addProjectMember(id, pickUser, pickRole);
      setPickUser(null);
      await refresh();
      toast.success(t("tm.projects.detail.memberAdded", "Member added."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // Hooks must run on every render and in the same order — keep them above
  // the early-return for the loading state, otherwise React throws
  // "Rendered more hooks than during the previous render".
  // taskCounts moved to a dedicated block right under visibleTasks below so
  // it reads from the scoped task set rather than the raw project total.

  /**
   * Visibility tier: TM admins see every task on the project, anyone else
   * sees just the ones they're on (assigned to them or that they created).
   * Everything below (filters, counts, board view) derives from this.
   */
  // The project owner is effectively an admin of *their* project — they wired
  // it up, so they need the full board view (and the assignee filter) even
  // without the global tm:admin grant.
  const isProjectOwner = !!project && !!myId && project.ownerUserId === myId;
  const canSeeAll = isTmAdmin || isProjectOwner;

  const visibleTasks = useMemo(() => {
    if (canSeeAll) return tasks;
    if (!myId) return [];
    return tasks.filter((tk) => tk.assigneeUserId === myId || tk.createdByUserId === myId);
  }, [tasks, canSeeAll, myId]);

  const taskCounts = useMemo(() => {
    const c: Record<TmTaskStatus | "ALL", number> = {
      ALL: visibleTasks.length,
      TODO: 0, IN_PROGRESS: 0, IN_REVIEW: 0, DONE: 0, BLOCKED: 0, CANCELLED: 0,
    };
    for (const tk of visibleTasks) c[tk.status]++;
    return c;
  }, [visibleTasks]);

  const filteredTasks = useMemo(() => {
    return visibleTasks.filter((tk) => {
      if (taskStatusFilter !== "ALL" && tk.status !== taskStatusFilter) return false;
      if (assigneeFilter.size > 0 && !assigneeFilter.has(tk.assigneeUserId)) return false;
      return true;
    });
  }, [visibleTasks, taskStatusFilter, assigneeFilter]);

  /** Unique assignees seen on visible tasks — drives the filter chip list. */
  const assigneeOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const tk of visibleTasks) {
      if (tk.assigneeUserId && !m.has(tk.assigneeUserId)) {
        m.set(tk.assigneeUserId, tk.assigneeName);
      }
    }
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [visibleTasks]);

  if (!project) {
    return <div style={{ padding: 24, color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>;
  }

  const availableUsers = users.filter((u) => !members.some((m) => m.userId === u.id));
  const TASK_STATUSES: TmTaskStatus[] = ["TODO", "IN_PROGRESS", "IN_REVIEW", "DONE", "BLOCKED"];

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Link to="/tm/projects" style={{ color: "var(--ink-3)", fontSize: 13, textDecoration: "none" }}>
          ← {t("tm.projects.backToList", "Back to projects")}
        </Link>
      </div>

      {/* Compact header — status, owner, dates, member count, and quick CTAs. */}
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{project.name}</h1>
              {project.description && (
                <p style={{ margin: "6px 0 0", color: "var(--ink-3)", fontSize: 13.5, whiteSpace: "pre-wrap" }}>
                  {project.description}
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <TmProjectStatusDropdown
                disabled={!canWrite || !(isTmAdmin || isProjectOwner)}
                value={project.status}
                onChange={async (newStatus) => {
                  if (!canWrite || !(isTmAdmin || isProjectOwner)) return;
                  const prevStatus = project.status;
                  setProject({ ...project, status: newStatus });
                  try {
                    const updated = await tmApi.updateProject(project.id, { status: newStatus });
                    setProject(updated);
                    toast.success(t("tm.projects.detail.statusSaved", "Status updated."));
                  } catch (err) {
                    toast.error((err as ApiError).message);
                    setProject({ ...project, status: prevStatus });
                  }
                }}
              />
              <button
                type="button"
                className="btn sm"
                onClick={() => setShowMembers(true)}
                style={{ height: 32 }}
              >
                👥 {t("tm.projects.detail.members", "Members")} ({members.length})
              </button>
              {canCreateTask && <Link
                to={`/tm/tasks/new?projectId=${project.id}`}
                className="btn primary sm"
                style={{ height: 32, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
              >
                + {t("tm.projects.detail.newTask", "Add task")}
              </Link>}
            </div>
          </div>

          {/* Meta chips — owner, team, dates — compact and equal weight. */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <MetaChip label={t("tm.projects.detail.owner", "Owner")} value={project.ownerName} accent="var(--brand-primary)" highlight />
            <MetaChip label={t("tm.projects.detail.team", "Team")} value={project.teamName ?? "—"} />
            <MetaChip label={t("tm.projects.detail.start", "Start")} value={project.startDate ?? "—"} />
            <MetaChip label={t("tm.projects.detail.targetEnd", "Target end")} value={project.targetEndDate ?? "—"} />
          </div>
        </div>
      </div>

      {/* Tasks — the main thing in this page. */}
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
              {t("tm.projects.detail.tasks", "Tasks")}
              <span style={{ marginLeft: 8, padding: "1px 8px", background: "var(--surface-2)", borderRadius: 999, fontSize: 12 }}>
                {tasks.length}
              </span>
            </h2>
            <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => setTaskView("list")}
                style={{
                  padding: "6px 12px", fontSize: 12, fontWeight: 600,
                  background: taskView === "list" ? "var(--brand-primary)" : "var(--surface)",
                  color: taskView === "list" ? "var(--on-primary)" : "var(--ink-2)",
                  border: "none", cursor: "pointer",
                }}
              >☰ {t("tm.projects.detail.viewList", "List")}</button>
              <button
                type="button"
                onClick={() => setTaskView("board")}
                style={{
                  padding: "6px 12px", fontSize: 12, fontWeight: 600,
                  background: taskView === "board" ? "var(--brand-primary)" : "var(--surface)",
                  color: taskView === "board" ? "var(--on-primary)" : "var(--ink-2)",
                  border: "none", cursor: "pointer", borderLeft: "1px solid var(--border)",
                }}
              >▦ {t("tm.projects.detail.viewBoard", "Board")}</button>
            </div>
          </div>

          {!canSeeAll && (
            <div style={{ fontSize: 11.5, color: "var(--ink-3)", background: "var(--surface-2)", border: "1px solid var(--border)", padding: "6px 10px", borderRadius: 6 }}>
              {t("tm.projects.detail.scopedToMe", "Showing tasks assigned to you or created by you. The project owner and TM admins see the full board.")}
            </div>
          )}

          {visibleTasks.length === 0 ? (
            <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{t("tm.projects.detail.noTasks", "No tasks linked yet.")}</div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <FilterChip
                  active={taskStatusFilter === "ALL"}
                  onClick={() => setTaskStatusFilter("ALL")}
                  label={t("tm.projects.detail.filterAll", { defaultValue: "All {{count}}", count: taskCounts.ALL })}
                />
                {TASK_STATUSES.map((s) => {
                  const n = taskCounts[s];
                  if (n === 0 && taskStatusFilter !== s) return null;
                  return (
                    <FilterChip
                      key={s}
                      active={taskStatusFilter === s}
                      onClick={() => setTaskStatusFilter(taskStatusFilter === s ? "ALL" : s)}
                      label={`${t(`tm.status.${s}`, { defaultValue: statusLabel(s) })} ${n}`}
                    />
                  );
                })}
              </div>

              {canSeeAll && assigneeOptions.length >= 1 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>
                    {t("tm.projects.detail.assigneeFilter", "Assignee")}
                  </span>
                  <FilterChip
                    active={assigneeFilter.size === 0}
                    onClick={() => setAssigneeFilter(new Set())}
                    label={t("tm.projects.detail.assigneeAll", "All")}
                  />
                  {assigneeOptions.map((a) => (
                    <FilterChip
                      key={a.id}
                      active={assigneeFilter.has(a.id)}
                      onClick={() => setAssigneeFilter((prev) => {
                        const next = new Set(prev);
                        if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                        return next;
                      })}
                      label={a.name}
                    />
                  ))}
                </div>
              )}

              {taskView === "list" ? (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                  {filteredTasks.map((tk) => (
                    <li key={tk.id} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px", background: "var(--surface-2)",
                      border: "1px solid var(--border)", borderRadius: 8,
                    }}>
                      <Link to={`/tm/tasks/${tk.id}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tk.title}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                          {tk.assigneeName}
                          {tk.dueDate ? t("tm.projects.detail.rowDue", { defaultValue: " · due {{date}}", date: tk.dueDate }) : ""}
                        </div>
                      </Link>
                      <TmPriorityPill priority={tk.priority} />
                      <TmStatusBadge status={tk.status} />
                    </li>
                  ))}
                  {filteredTasks.length === 0 && (
                    <li style={{ color: "var(--ink-4)", fontSize: 12.5, padding: "8px 0" }}>
                      {t("tm.projects.detail.noTasksFiltered", "No tasks match this filter.")}
                    </li>
                  )}
                </ul>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10 }}>
                  {TASK_STATUSES.map((status) => {
                    const colTasks = filteredTasks.filter((tk) => tk.status === status);
                    return (
                      <div key={status} style={{
                        background: "var(--surface-2)", border: "1px solid var(--border)",
                        borderRadius: 8, padding: 8, minHeight: 220,
                      }}>
                        <header style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "2px 4px 8px", color: "var(--ink-3)",
                          fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
                        }}>
                          <span>{t(`tm.status.${status}`, { defaultValue: statusLabel(status) })}</span>
                          <span style={{
                            background: "var(--surface)", padding: "1px 7px",
                            borderRadius: 999, fontSize: 10,
                          }}>{colTasks.length}</span>
                        </header>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {colTasks.length === 0 ? (
                            <div style={{ color: "var(--ink-4)", fontSize: 11.5, padding: 6, textAlign: "center" }}>—</div>
                          ) : colTasks.map((tk) => (
                            <Link
                              key={tk.id}
                              to={`/tm/tasks/${tk.id}`}
                              style={{
                                background: "var(--surface)", border: "1px solid var(--border)",
                                borderRadius: 6, padding: "8px 10px",
                                textDecoration: "none", color: "var(--ink)",
                                display: "flex", flexDirection: "column", gap: 4,
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                                <span style={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.3 }}>{tk.title}</span>
                                <TmPriorityPill priority={tk.priority} />
                              </div>
                              <span style={{ color: "var(--ink-3)", fontSize: 11 }}>
                                {tk.assigneeName}
                                {tk.dueDate ? t("tm.projects.detail.rowDate", { defaultValue: " · {{date}}", date: tk.dueDate }) : ""}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showMembers && (
        <div
          onClick={() => setShowMembers(false)}
          style={{
            position: "fixed", inset: 0, background: "var(--overlay)",
            display: "grid", placeItems: "center", zIndex: 50, padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--surface)", borderRadius: 12, padding: 22,
              width: "min(640px, 100%)", maxHeight: "85vh", overflowY: "auto",
              display: "flex", flexDirection: "column", gap: 14,
              boxShadow: "var(--shadow-2)",
            }}
          >
            <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>
                  {t("tm.projects.detail.members", "Members")} ({members.length})
                </h2>
                <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 12 }}>
                  {t("tm.projects.detail.membersHint", "People with access to this project.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowMembers(false)}
                aria-label={t("tm.projects.detail.close", "Close")}
                style={{ background: "transparent", border: "none", fontSize: 24, color: "var(--ink-3)", cursor: "pointer", lineHeight: 1, padding: 0 }}
              >×</button>
            </header>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 160px auto", gap: 8, alignItems: "end" }}>
              <UserCombobox users={availableUsers} value={pickUser} onChange={setPickUser} />
              <Select<TmProjectMemberRole>
                value={pickRole}
                onChange={setPickRole}
                options={[
                  { value: "OWNER", label: t("tm.project.memberRole.OWNER", "Owner") },
                  { value: "CONTRIBUTOR", label: t("tm.project.memberRole.CONTRIBUTOR", "Contributor") },
                  { value: "VIEWER", label: t("tm.project.memberRole.VIEWER", "Viewer") },
                ]}
              />
              <button type="button" className="btn primary" onClick={addMember} disabled={!canFull || !(isTmAdmin || isProjectOwner) || !pickUser}>
                {t("tm.projects.detail.addMember", "Add member")}
              </button>
            </div>

            {members.length === 0 ? (
              <div style={{ color: "var(--ink-3)", fontSize: 13, padding: "20px 0", textAlign: "center" }}>
                {t("tm.projects.detail.noMembers", "No members yet.")}
              </div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {members.map((m) => (
                  <li key={m.userId} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", background: "var(--surface-2)",
                    border: "1px solid var(--border)", borderRadius: 8, fontSize: 13,
                  }}>
                    <span aria-hidden style={{
                      width: 30, height: 30, borderRadius: "50%",
                      background: "var(--brand-primary)", color: "var(--on-primary)",
                      display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, flex: "none",
                    }}>{m.name.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "var(--ink)" }}>{m.name}</div>
                      <div style={{ color: "var(--ink-3)", fontSize: 11 }}>{m.email}</div>
                    </div>
                    <span style={{
                      padding: "2px 8px", borderRadius: 6,
                      background: "var(--surface)", color: "var(--ink-2)",
                      fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
                    }}>
                      {t(`tm.project.memberRole.${m.role}`, { defaultValue: m.role })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MetaChip({ label, value, accent, highlight }: { label: string; value: string; accent?: string; highlight?: boolean }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "6px 12px",
      background: highlight ? "var(--brand-primary-50)" : "var(--surface-muted)",
      border: `1px solid ${highlight ? (accent ?? "var(--brand-primary)") + "33" : "var(--border)"}`,
      borderRadius: 8,
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, color: "var(--ink-4)",
        textTransform: "uppercase", letterSpacing: 0.4,
      }}>{label}</span>
      <strong style={{ fontSize: 13, color: accent ?? "var(--ink)" }}>{value}</strong>
    </div>
  );
}

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? "var(--brand-primary)" : "var(--surface)",
        color: active ? "var(--on-primary)" : "var(--ink-2)",
        border: `1px solid ${active ? "var(--brand-primary)" : "var(--border)"}`,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function statusLabel(s: TmTaskStatus): string {
  switch (s) {
    case "TODO":        return "To do";
    case "IN_PROGRESS": return "In progress";
    case "IN_REVIEW":   return "In review";
    case "BLOCKED":     return "Blocked";
    case "DONE":        return "Done";
    case "CANCELLED":   return "Cancelled";
  }
}
